import React from 'react';
import { X } from 'lucide-react';

interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] animate-fade-in">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 relative max-h-[80vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="sticky float-right right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-1 z-10"
        >
          <X size={24} />
        </button>

        <div className="p-8 pt-4">
          <h2 className="font-serif text-2xl font-bold text-primary-900 mb-6">Privacy Policy</h2>
          
          <div className="prose max-w-none">
            <p className="text-gray-600 mb-6">
              Last updated: {new Date().toLocaleDateString('en-GB')}
            </p>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">1. Introduction</h3>
              <p className="text-gray-600 mb-4">
                SL Rooms ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">2. Information We Collect</h3>
              <h4 className="font-medium mb-2">2.1 Personal Information</h4>
              <p className="text-gray-600 mb-4">
                We may collect personal information that you voluntarily provide, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600">
                <li>Name and contact information</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Postal address</li>
                <li>Booking preferences and requirements</li>
              </ul>

              <h4 className="font-medium mb-2">2.2 Automatically Collected Information</h4>
              <p className="text-gray-600 mb-4">
                When you visit our website, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Usage data</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">3. How We Use Your Information</h3>
              <p className="text-gray-600 mb-4">
                We use the collected information for various purposes, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600">
                <li>Processing your bookings and enquiries</li>
                <li>Communicating with you about our services</li>
                <li>Improving our website and services</li>
                <li>Sending promotional materials (with your consent)</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">4. Data Protection</h3>
              <p className="text-gray-600 mb-4">
                We implement appropriate technical and organisational measures to maintain the security of your personal information. However, no Internet-based site can be 100% secure, and we cannot be held responsible for unauthorised or unintended access that is beyond our control.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">5. Your Rights</h3>
              <p className="text-gray-600 mb-4">
                Under the General Data Protection Regulation (GDPR), you have certain rights regarding your personal data, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-600">
                <li>The right to access your personal data</li>
                <li>The right to rectification</li>
                <li>The right to erasure</li>
                <li>The right to restrict processing</li>
                <li>The right to data portability</li>
                <li>The right to object</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-semibold mb-4">6. Contact Us</h3>
              <p className="text-gray-600 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="text-gray-600">
                <p>SL Rooms Limited</p>
                <p>1 London Road</p>
                <p>Ipswich, Suffolk, IP1 2HA</p>
                <p>United Kingdom</p>
                <p>Email: info@slrooms.com</p>
                <p>Phone: +44 808 531 0127</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;